/**
 * `/cli/authorize` — 「この端末の Kumo CLI を許可」の画面（pages/CliAuthorize.tsx）の「React に依存しない部分」。
 * 2026-09-28 creator/connect。node --test で確かめる（cliAuthorize.test.ts）。packages/cli の connect.test.ts も本物のサーバーとの通しでこれを使う。
 *
 * 往復（サーバーの正は apps/server/src/modules/platform/cliGrants.ts・CLI は packages/cli/src/commands/cliLogin.ts）:
 *   1. CLI（`xkumo login` / `xkumo connect`）が 127.0.0.1 のランダムなポートで待ち、ブラウザで
 *      `/cli/authorize?port=<port>&s=<state>&challenge=<challenge>&device=<端末の名前>` を開く
 *   2. 本人（ログイン済み・メール確認済み）が「許可」→ POST /v1/developers/cli-grants { challenge, redirectUrl: http://127.0.0.1:<port>/callback?s=<state> }
 *      → 応答の redirectUrl（`&code=…` 付き）へ移る。**行き先はサーバーが 127.0.0.1 の形に固定して検査する**（requireCliRedirect）。
 *      ここでも移る前に同じ形か確かめる（isCliCallback）= サーバーの応答が壊れていても別の場所へ code を運ばない
 *   3. 「やめる」→ 同じ戻り先に `error=access_denied` を付けて移る（CLI が 5 分待ち続けないように）。code は無い
 *
 * WHY URL の値を厳しく読むか: port / s / challenge / device は URL から来る = 他人が作ったリンクでもよい値。形が違えば画面を出さない
 *   （「このリンクは使えません」）。サーバーの検査（STATE_RE・CHALLENGE_RE）と同じ形にそろえ、押してから 400 になるのを防ぐ。
 * WHY 他人のリンクでも大きな害にならないか: code は押した人の PC の 127.0.0.1 に行く（攻撃者の手元には届かない）。それでも
 *   その PC で待っている別のプログラムに渡る可能性はあるので、画面は「自分で実行していなければ押さない」を目立たせる（指示書）。
 * device（端末の名前）は CLI が申告する表示だけ（偽れる）。サーバーには送らない。
 */

/** 画面のパス（packages/cli の CLI_AUTHORIZE_PATH と同じ。変えるなら両方） */
export const CLI_AUTHORIZE_PATH = '/cli/authorize';

export interface CliAuthorizeRequest {
  port: number;
  state: string;
  challenge: string;
  /** 端末の名前（表示だけ）。無ければ null */
  device: string | null;
}

/** サーバーの cliGrants.ts の STATE_RE と同じ */
const STATE_RE = /^[A-Za-z0-9_-]{16,128}$/;
/** サーバーの grantCodes.ts の CHALLENGE_RE と同じ（base64url(sha256(verifier)) = 43 文字） */
const CHALLENGE_RE = /^[A-Za-z0-9_-]{43}$/;

/** 端末の名前: 制御文字を落として 64 文字まで（CLI の deviceName と同じ規則）。空なら null */
export function cleanDevice(raw: string | null): string | null {
  if (raw === null) return null;
  // eslint-disable-next-line no-control-regex
  const s = raw.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, 64);
  return s || null;
}

/** URL のクエリ（location.search）→ 許可の要求。形が違えば null（画面は「このリンクは使えません」） */
export function parseCliAuthorize(search: string): CliAuthorizeRequest | null {
  const q = new URLSearchParams(search);
  const portRaw = q.get('port') ?? '';
  const state = q.get('s') ?? '';
  const challenge = q.get('challenge') ?? '';
  if (!/^\d{1,5}$/.test(portRaw)) return null;
  const port = Number(portRaw);
  if (port < 1 || port > 65535) return null;
  if (!STATE_RE.test(state) || !CHALLENGE_RE.test(challenge)) return null;
  return { port, state, challenge, device: cleanDevice(q.get('device')) };
}

/** POST /v1/developers/cli-grants に送る戻り先（サーバーが受ける唯一の形: http://127.0.0.1:<port>/callback?s=<state>） */
export function cliRedirectUrl(req: CliAuthorizeRequest): string {
  return `http://127.0.0.1:${req.port}/callback?s=${encodeURIComponent(req.state)}`;
}

/** 「やめる」の行き先（code は無い。CLI は access_denied で止まる） */
export function cliDenyUrl(req: CliAuthorizeRequest): string {
  return `${cliRedirectUrl(req)}&error=access_denied`;
}

/**
 * サーバーの応答の redirectUrl が、この要求の 127.0.0.1 の戻り先（code 付き）か。違えば移らない（上の 2 の WHY）
 */
export function isCliCallback(raw: unknown, req: CliAuthorizeRequest): raw is string {
  if (typeof raw !== 'string') return false;
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return false;
  }
  return (
    u.protocol === 'http:' &&
    u.hostname === '127.0.0.1' &&
    u.port === String(req.port) &&
    u.pathname === '/callback' &&
    !u.username &&
    !u.password &&
    !u.hash &&
    u.searchParams.get('s') === req.state &&
    Boolean(u.searchParams.get('code'))
  );
}
